import { v4 as uuidv4 } from 'uuid'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '../store'
import { getViews, createView, deleteView as deleteViewAPI } from '../lib/api'

type ViewState = {
  views: APIView[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: ViewState = {
  views: [],
  loaded: false,
  loading: false,
  error: null
}

export const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    loading: (state: ViewState) => {
      state.loading = true
    },

    load: (state: ViewState, action: PayloadAction<APIView[]>) => {
      state.views = action.payload
      state.loaded = true
      state.loading = false
    },

    add: (state: ViewState, action: PayloadAction<APIView>) => {
      state.views.push(action.payload)
    },

    failed: (state: ViewState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loading = false
      state.loaded = true
    },

    removeView: (state: ViewState, action: PayloadAction<string>) => {
      state.views = state.views.filter(view => view.id !== action.payload)
    }
  }
})

const { loading, load, add, failed, removeView } = viewsSlice.actions

// Asynchronous thunk action
export const fetchViews = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(loading())

    try {
      const views = await getViews()
      dispatch(load(views))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const addView = <T = APIInboxViewFilter>(
  data: ViewUpdate<T>,
  temp: boolean,
  cb: (x: APIView) => void
) => {
  return async (dispatch: AppDispatch) => {
    try {
      let newView: APIView
      if (temp) {
        const now = new Date().toISOString()
        newView = {
          ...data,
          hidden: true,
          id: uuidv4(),
          created_at: now,
          updated_at: now
        }
      } else {
        newView = await createView(data)
      }

      dispatch(add(newView))
      cb(newView)
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const deleteView = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await deleteViewAPI(id)
      dispatch(removeView(id))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const selectViews = (state: RootState) => state.views

export default viewsSlice.reducer
